import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Box, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import BoltIcon from '@mui/icons-material/Bolt';
import dayjs from 'dayjs';

import AuthContext from '../../context/authContext';
import useIssue from '../../hooks/useIssue';
import useOutage from '../../hooks/useOutage';
import { ISSUE_PRIORITY_BY_KEY, formatDurationMs } from '../../util/issueConstants';

// Presentation is deliberately cinematic and always dark, independent of the
// app theme. Cards auto-advance; the audience perspective (Arenaleiter / IT /
// Geschäftsführung) filters and reshapes the deck.
const CARD_INTERVAL_MS = 12000;

const PERSPECTIVES = [
  { key: 'arena', label: 'Arenaleiter', accent: '#ffd54f' },
  { key: 'it', label: 'IT', accent: '#4fc3f7' },
  { key: 'gf', label: 'Geschäftsführung', accent: '#aed581' },
];

// Count-up animation for hero numbers.
const useCountUp = (target, durationMs = 900) => {
  const [value, setValue] = useState(0);
  useEffect(() => {
    const numeric = Number(target) || 0;
    const start = performance.now();
    let frame;
    const tick = now => {
      const progress = Math.min(1, (now - start) / durationMs);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(numeric * eased));
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target, durationMs]);
  return value;
};

const HeroNumber = ({ value, color }) => {
  const animated = useCountUp(value);
  return (
    <Typography
      component='span'
      sx={{
        fontSize: 'clamp(64px, 14vw, 170px)',
        fontWeight: 800,
        lineHeight: 1,
        color: color || '#fff',
        textShadow: '0 0 60px rgba(255,255,255,0.15)',
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {animated}
    </Typography>
  );
};

const cardBaseSx = {
  position: 'relative',
  width: 'min(1280px, 95vw)',
  minHeight: 'min(700px, 78vh)',
  borderRadius: 6,
  p: { xs: 4, md: 8 },
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  backdropFilter: 'blur(14px)',
  background: 'linear-gradient(145deg, rgba(255,255,255,0.09), rgba(255,255,255,0.03))',
  border: '1px solid rgba(255,255,255,0.14)',
  boxShadow: '0 40px 120px rgba(0,0,0,0.55)',
  overflow: 'hidden',
  animation: 'fzCardIn 700ms cubic-bezier(0.22, 1, 0.36, 1) both',
  '@keyframes fzCardIn': {
    from: { opacity: 0, transform: 'translateY(48px) scale(0.96)' },
    to: { opacity: 1, transform: 'translateY(0) scale(1)' },
  },
};

const AccentBlob = ({ color, sx }) => (
  <Box
    sx={{
      position: 'absolute',
      width: 420,
      height: 420,
      borderRadius: '50%',
      filter: 'blur(90px)',
      opacity: 0.35,
      background: color,
      animation: 'fzFloat 9s ease-in-out infinite alternate',
      '@keyframes fzFloat': {
        from: { transform: 'translateY(-16px)' },
        to: { transform: 'translateY(20px)' },
      },
      pointerEvents: 'none',
      ...sx,
    }}
  />
);

// Time window every card belongs to. The deck mixes the current state with
// 90-day statistics, and a bare hero number does not say which one it is - the
// kicker states it on every card in the same place and wording.
const SCOPE_LABELS = {
  live: 'Jetzt',
  '90d': '90 Tage',
  week: 'Diese Woche',
};

const CardKicker = ({ icon, text, accent, scope }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
    {icon}
    <Typography
      sx={{
        textTransform: 'uppercase',
        letterSpacing: '0.35em',
        fontSize: 14,
        fontWeight: 700,
        color: accent,
      }}
    >
      {text}
    </Typography>
    {scope && SCOPE_LABELS[scope] && (
      <Typography
        sx={{
          textTransform: 'uppercase',
          letterSpacing: '0.28em',
          fontSize: 13,
          fontWeight: 600,
          color: 'rgba(255,255,255,0.45)',
        }}
      >
        · {SCOPE_LABELS[scope]}
      </Typography>
    )}
  </Box>
);

// Renders one presentation card by type.
const PresentationCard = ({ card, accent }) => {
  if (card.type === 'issue') {
    const priority = ISSUE_PRIORITY_BY_KEY[card.issue.priority] || {};
    const isP1 = card.issue.priority === 'p1';
    return (
      <Box
        sx={{
          ...cardBaseSx,
          ...(isP1 && {
            animation:
              'fzCardIn 700ms cubic-bezier(0.22,1,0.36,1) both, fzGlow 2.4s ease-in-out infinite',
            '@keyframes fzCardIn': cardBaseSx['@keyframes fzCardIn'],
            '@keyframes fzGlow': {
              '0%, 100%': { boxShadow: `0 40px 120px rgba(0,0,0,0.55), 0 0 0 rgba(211,47,47,0)` },
              '50%': { boxShadow: `0 40px 120px rgba(0,0,0,0.55), 0 0 90px rgba(211,47,47,0.45)` },
            },
          }),
        }}
      >
        <AccentBlob color={priority.color} sx={{ top: -140, right: -120 }} />
        <CardKicker
          icon={<WarningAmberIcon sx={{ color: priority.color }} />}
          text={priority.label || card.issue.priority}
          accent={priority.color}
          scope='live'
        />
        <Typography
          sx={{
            fontSize: 'clamp(34px, 5vw, 64px)',
            fontWeight: 800,
            lineHeight: 1.1,
            color: '#fff',
            mb: 2,
          }}
        >
          {card.issue.title}
        </Typography>
        <Typography
          sx={{ fontSize: 'clamp(16px, 2vw, 24px)', color: 'rgba(255,255,255,0.75)', mb: 3 }}
        >
          {[
            card.issue.branchId?.locationName || card.issue.branchId?.longName,
            card.issue.entertainmentId?.longName || card.issue.entertainmentId?.entertainmentName,
            card.issue.resourceId?.name,
          ]
            .filter(Boolean)
            .join('  ·  ')}
        </Typography>
        {card.issue.description && (
          <Typography
            sx={{
              fontSize: 18,
              color: 'rgba(255,255,255,0.6)',
              mb: 3,
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {card.issue.description}
          </Typography>
        )}
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          {card.issue.categoryId?.name && (
            <Chip
              label={card.issue.categoryId.name}
              sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.4)' }}
              variant='outlined'
            />
          )}
          <Chip
            label={`Gemeldet ${dayjs(card.issue.meta?.createdAt).format('DD.MM. HH:mm')} von ${card.issue.reportedByUsername || '–'}`}
            sx={{ color: 'rgba(255,255,255,0.8)', borderColor: 'rgba(255,255,255,0.25)' }}
            variant='outlined'
          />
          {card.issue.assignedUsername && (
            <Chip
              label={`Übernommen: ${card.issue.assignedUsername}`}
              sx={{ color: '#0a0a0a', bgcolor: accent, fontWeight: 700 }}
            />
          )}
        </Box>
      </Box>
    );
  }

  if (card.type === 'kpi') {
    return (
      <Box sx={cardBaseSx}>
        <AccentBlob color={accent} sx={{ bottom: -160, left: -120 }} />
        <CardKicker
          icon={<BoltIcon sx={{ color: accent }} />}
          text={card.kicker}
          accent={accent}
          scope={card.scope}
        />
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 3, flexWrap: 'wrap' }}>
          {card.big !== undefined ? (
            <HeroNumber value={card.big} color={card.bigColor || '#fff'} />
          ) : (
            <Typography
              sx={{ fontSize: 'clamp(48px, 8vw, 110px)', fontWeight: 800, color: '#fff' }}
            >
              {card.bigText}
            </Typography>
          )}
          <Typography sx={{ fontSize: 'clamp(20px, 3vw, 34px)', color: 'rgba(255,255,255,0.75)' }}>
            {card.unit}
          </Typography>
        </Box>
        <Typography
          sx={{ fontSize: 'clamp(20px, 2.6vw, 32px)', color: '#fff', fontWeight: 600, mt: 2 }}
        >
          {card.title}
        </Typography>
        {card.subtitle && (
          <Typography sx={{ fontSize: 18, color: 'rgba(255,255,255,0.6)', mt: 1 }}>
            {card.subtitle}
          </Typography>
        )}
      </Box>
    );
  }

  // 'list' card: ranked rows with animated bars.
  return (
    <Box sx={cardBaseSx}>
      <AccentBlob color={accent} sx={{ top: -160, right: -140 }} />
      <CardKicker
          icon={<BoltIcon sx={{ color: accent }} />}
          text={card.kicker}
          accent={accent}
          scope={card.scope}
        />
      <Typography
        sx={{ fontSize: 'clamp(28px, 4vw, 48px)', fontWeight: 800, color: '#fff', mb: 4 }}
      >
        {card.title}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {card.rows.slice(0, 7).map((row, index) => {
          const percent = Number(row.percent) || 0;
          return (
            <Box key={row.label + index} sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Typography
                sx={{
                  flex: '0 0 auto',
                  width: { xs: 160, md: 360 },
                  color: 'rgba(255,255,255,0.85)',
                  fontSize: { xs: 15, md: 22 },
                  fontWeight: 600,
                  lineHeight: 1.25,
                  // Two lines instead of a hard ellipsis: on a wall screen
                  // nobody can hover a truncated label to read the rest.
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {row.label}
              </Typography>
              <Box
                sx={{
                  flexGrow: 1,
                  minWidth: 0,
                  height: 14,
                  borderRadius: 7,
                  bgcolor: 'rgba(255,255,255,0.08)',
                  overflow: 'hidden',
                }}
              >
                {/* An empty row stays empty - a minimum-width stub would read
                    as "there is something here" for a count of zero. */}
                {percent > 0 && (
                  <Box
                    sx={{
                      height: '100%',
                      borderRadius: 7,
                      width: `${Math.min(100, Math.max(2, percent))}%`,
                      background: row.color || accent,
                      animation: 'fzBarIn 900ms cubic-bezier(0.22,1,0.36,1) both',
                      animationDelay: `${index * 120}ms`,
                      '@keyframes fzBarIn': { from: { width: 0 }, to: {} },
                    }}
                  />
                )}
              </Box>
              <Typography
                sx={{
                  flex: '0 0 auto',
                  minWidth: 90,
                  textAlign: 'right',
                  color: '#fff',
                  fontWeight: 700,
                  fontVariantNumeric: 'tabular-nums',
                  fontSize: { xs: 15, md: 22 },
                  // Values are numbers as well as short texts ("seit 10.08.
                  // 10:44"); those must not wrap into three lines.
                  whiteSpace: 'nowrap',
                }}
              >
                {row.value}
              </Typography>
            </Box>
          );
        })}
        {card.rows.length === 0 && (
          <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 20 }}>
            {card.emptyText || 'Alles im grünen Bereich. 🎉'}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

// Fullscreen presentation mode: animated cards over the live issue/outage data,
// switchable between the three audience perspectives.
const IssuePresentationView = () => {
  const authCtx = useContext(AuthContext);
  const navigate = useNavigate();
  const { getIssues, getStatistics } = useIssue();
  const { getOutages } = useOutage();

  const [issues, setIssues] = useState([]);
  const [outages, setOutages] = useState([]);
  const [stats, setStats] = useState(null);
  const [perspective, setPerspective] = useState('arena');
  const [cardIndex, setCardIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [clock, setClock] = useState(dayjs());
  const rootRef = useRef(null);

  const canRead = authCtx.checkRight('issue:read');

  const load = useCallback(async () => {
    try {
      const [issueResponse, outageResponse, statsResponse] = await Promise.all([
        getIssues({}).catch(() => null),
        getOutages().catch(() => null),
        getStatistics({}).catch(() => null),
      ]);
      if (issueResponse) setIssues(issueResponse.issues || []);
      if (outageResponse)
        setOutages((outageResponse.outages || []).filter(o => o.status === 'active'));
      if (statsResponse) setStats(statsResponse);
    } catch {
      // toasts handled in hooks
    }
  }, [getIssues, getOutages, getStatistics]);

  useEffect(() => {
    load();
    const interval = setInterval(load, 60000);
    return () => clearInterval(interval);
  }, [load]);

  useEffect(() => {
    const interval = setInterval(() => setClock(dayjs()), 1000);
    return () => clearInterval(interval);
  }, []);

  // Build the deck for the active perspective from a shared metric set.
  const cards = useMemo(() => {
    const accentByPriority = key => ISSUE_PRIORITY_BY_KEY[key]?.color;
    const openIssues = issues.filter(issue => !['resolved', 'cancelled'].includes(issue.status));
    const p1Issues = openIssues.filter(issue => issue.priority === 'p1');
    const now = Date.now();

    // --- shared metrics -----------------------------------------------------
    const byPrio = key => (stats?.byPriority || []).find(row => row._id === key);
    const weekly = stats?.weeklySeries || [];
    const created90 = weekly.reduce((sum, row) => sum + (row.created || 0), 0);
    const resolved90 = weekly.reduce((sum, row) => sum + (row.resolved || 0), 0);
    const p1Count90 = weekly.reduce((sum, row) => sum + (row.p1 || 0), 0);
    const resolutionRate = created90 > 0 ? Math.round((resolved90 / created90) * 100) : null;
    const thisWeek = weekly[weekly.length - 1];
    const prevWeek = weekly[weekly.length - 2];
    const weekDelta =
      thisWeek && prevWeek && prevWeek.created > 0
        ? Math.round(((thisWeek.created - prevWeek.created) / prevWeek.created) * 100)
        : null;

    const reactionRows = (stats?.byPriority || []).filter(row => row.avgReactionMs);
    const avgReaction =
      reactionRows.length > 0
        ? reactionRows.reduce((sum, row) => sum + row.avgReactionMs, 0) / reactionRows.length
        : null;
    const resolutionRows = (stats?.byPriority || []).filter(row => row.avgResolutionMs);
    const mttr =
      resolutionRows.length > 0
        ? resolutionRows.reduce((sum, row) => sum + row.avgResolutionMs, 0) / resolutionRows.length
        : null;
    const mttrP1 = byPrio('p1')?.avgResolutionMs || null;
    const mttrP2 = byPrio('p2')?.avgResolutionMs || null;
    const reactionP1 = byPrio('p1')?.avgReactionMs || null;

    const outageStats = stats?.outageStats || [];
    const downtimeTotal = outageStats.reduce((sum, row) => sum + (row.totalDowntimeMs || 0), 0);
    const outageCount90 = outageStats.reduce((sum, row) => sum + (row.outages || 0), 0);
    const avgDowntimePerOutage = outageCount90 > 0 ? downtimeTotal / outageCount90 : null;
    const longestActiveOutage = outages.reduce((max, outage) => {
      const duration = now - new Date(outage.startedAt).getTime();
      return duration > (max?.duration || 0) ? { outage, duration } : max;
    }, null);

    const oldestOpenIssue = openIssues.reduce((max, issue) => {
      const age = now - new Date(issue.meta?.createdAt || now).getTime();
      return age > (max?.age || 0) ? { issue, age } : max;
    }, null);

    const escalation = stats?.escalationStats || [];
    const escalationTotal = escalation.reduce((sum, row) => sum + (row.count || 0), 0);
    const escalationAck = escalation.find(row => row._id === 'acknowledged');
    const escalationExhausted = escalation.find(row => row._id === 'exhausted')?.count || 0;

    const byCategory = new Map();
    openIssues.forEach(issue => {
      const key = issue.categoryId?.name || 'Ohne Kategorie';
      byCategory.set(key, (byCategory.get(key) || 0) + 1);
    });
    const maxCategory = Math.max(1, ...byCategory.values());
    const categoryRows = [...byCategory.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([label, count]) => ({ label, value: count, percent: (count / maxCategory) * 100 }));

    const openByPriorityRows = ['p1', 'p2', 'p3', 'p4', 'p5'].map(key => {
      const count = openIssues.filter(issue => issue.priority === key).length;
      const maxOpen = Math.max(1, openIssues.length);
      return {
        label: ISSUE_PRIORITY_BY_KEY[key].label,
        value: count,
        percent: (count / maxOpen) * 100,
        color: accentByPriority(key),
      };
    });

    // The bar length encodes how long the outage has been running relative to
    // the longest one, so the row reads the same way as every other list card
    // instead of showing a meaningless full bar for each entry.
    const maxOutageAge = Math.max(
      1,
      ...outages.map(outage => now - new Date(outage.startedAt).getTime())
    );
    const outageRows = outages.map(outage => ({
      label:
        outage.level === 'resource'
          ? outage.resourceId?.name || 'Raum'
          : outage.entertainmentId?.longName ||
            outage.entertainmentId?.entertainmentName ||
            'Entertainment',
      value: `seit ${dayjs(outage.startedAt).format('DD.MM. HH:mm')}`,
      percent: ((now - new Date(outage.startedAt).getTime()) / maxOutageAge) * 100,
      color: accentByPriority('p1'),
    }));

    const maxDowntime = Math.max(1, ...outageStats.map(row => row.totalDowntimeMs || 0));
    const downtimeRows = outageStats.slice(0, 7).map(row => ({
      label: row.resourceName || row.entertainmentName || 'Unbekannt',
      value: formatDurationMs(row.totalDowntimeMs || 0),
      percent: ((row.totalDowntimeMs || 0) / maxDowntime) * 100,
    }));

    // --- card library -------------------------------------------------------
    const kpiOpen = {
      type: 'kpi',
      kicker: 'Live-Status',
      scope: 'live',
      big: openIssues.length,
      bigColor: p1Issues.length > 0 ? accentByPriority('p1') : '#fff',
      unit: 'offene Issues',
      title:
        p1Issues.length > 0
          ? `${p1Issues.length} × P1 – sofortige Aufmerksamkeit nötig`
          : 'Kein P1 offen – alle Entertainments bespielbar',
      subtitle: `${outages.length} aktive Ausfälle · Stand ${dayjs().format('HH:mm')} Uhr`,
    };
    const cardOutages = {
      type: 'list',
      kicker: 'Verfügbarkeit',
      scope: 'live',
      title: 'Aktuell nicht bespielbar',
      rows: outageRows,
      emptyText: 'Alle Räume und Entertainments sind bespielbar. 🎉',
    };
    const cardOpenByPriority = {
      type: 'list',
      kicker: 'Live-Status',
      scope: 'live',
      title: 'Offene Issues nach Priorität',
      rows: openByPriorityRows,
    };
    const cardCategories = {
      type: 'list',
      kicker: 'Workload',
      scope: 'live',
      title: 'Offene Issues nach Spezialgebiet',
      rows: categoryRows,
      emptyText: 'Keine offenen Issues. 🎉',
    };
    const cardDowntimeRooms = {
      type: 'list',
      kicker: 'Verfügbarkeit',
      scope: '90d',
      title: 'Downtime nach Raum (90 Tage)',
      rows: downtimeRows,
      emptyText: 'Keine Ausfälle in den letzten 90 Tagen.',
    };
    const cardReactionP1 = {
      type: 'kpi',
      kicker: 'Reaktion',
      scope: '90d',
      bigText: reactionP1 ? formatDurationMs(reactionP1) : '–',
      unit: '',
      title: 'Ø Reaktionszeit auf P1-Störungen',
      subtitle: 'Meldung bis erste Reaktion · letzte 90 Tage',
    };
    const cardReactionAll = {
      type: 'kpi',
      kicker: 'Reaktion',
      scope: '90d',
      bigText: avgReaction ? formatDurationMs(avgReaction) : '–',
      unit: '',
      title: 'Ø Reaktionszeit über alle Prioritäten',
      subtitle: 'letzte 90 Tage',
    };
    const cardMttrP1 = {
      type: 'kpi',
      kicker: 'Instandhaltung',
      scope: '90d',
      bigText: mttrP1 ? formatDurationMs(mttrP1) : '–',
      unit: '',
      title: 'MTTR für P1 – nicht bespielbar',
      subtitle: 'Meldung bis Lösung · letzte 90 Tage',
    };
    const cardMttrP2 = {
      type: 'kpi',
      kicker: 'Instandhaltung',
      scope: '90d',
      bigText: mttrP2 ? formatDurationMs(mttrP2) : '–',
      unit: '',
      title: 'MTTR für P2 – defekt, aber bespielbar',
      subtitle: 'letzte 90 Tage',
    };
    const cardMttrAll = {
      type: 'kpi',
      kicker: 'Instandhaltung',
      scope: '90d',
      bigText: mttr ? formatDurationMs(mttr) : '–',
      unit: '',
      title: 'Ø Zeit bis zur Lösung (MTTR)',
      subtitle: 'über alle Prioritäten · letzte 90 Tage',
    };
    const cardDowntimeTotal = {
      type: 'kpi',
      kicker: 'Verfügbarkeit',
      scope: '90d',
      bigText: formatDurationMs(downtimeTotal),
      unit: '',
      title: 'Downtime der letzten 90 Tage',
      subtitle: `${outageCount90} Ausfälle insgesamt`,
    };
    const cardOutageCount = {
      type: 'kpi',
      kicker: 'Verfügbarkeit',
      scope: '90d',
      big: outageCount90,
      unit: 'Ausfälle',
      title: 'Ausfälle in den letzten 90 Tagen',
      subtitle: avgDowntimePerOutage
        ? `Ø Dauer je Ausfall: ${formatDurationMs(avgDowntimePerOutage)}`
        : undefined,
    };
    const cardLongestOutage = {
      type: 'kpi',
      kicker: 'Verfügbarkeit',
      scope: 'live',
      bigText: longestActiveOutage ? formatDurationMs(longestActiveOutage.duration) : '–',
      unit: '',
      title: longestActiveOutage
        ? `Längster aktiver Ausfall: ${
            longestActiveOutage.outage.resourceId?.name ||
            longestActiveOutage.outage.entertainmentId?.longName ||
            longestActiveOutage.outage.entertainmentId?.entertainmentName ||
            'Unbekannt'
          }`
        : 'Kein aktiver Ausfall',
      subtitle: longestActiveOutage?.outage?.reason || undefined,
      bigColor: longestActiveOutage ? accentByPriority('p1') : '#fff',
    };
    const cardCreated90 = {
      type: 'kpi',
      kicker: 'Meldungen',
      scope: '90d',
      big: created90,
      unit: 'Issues gemeldet',
      title: 'Meldungen der letzten 90 Tage',
      subtitle: `${resolved90} davon gelöst${resolutionRate !== null ? ` · Lösungsquote ${resolutionRate} %` : ''}`,
    };
    const cardResolutionRate = {
      type: 'kpi',
      kicker: 'Meldungen',
      scope: '90d',
      big: resolutionRate !== null ? resolutionRate : 0,
      unit: '% Lösungsquote',
      title: 'Anteil gelöster Issues',
      subtitle: `${resolved90} von ${created90} Meldungen · letzte 90 Tage`,
    };
    const cardP1Volume = {
      type: 'kpi',
      kicker: 'Kritische Störungen',
      scope: '90d',
      big: p1Count90,
      bigColor: p1Count90 > 0 ? accentByPriority('p1') : '#fff',
      unit: 'P1-Störungen',
      title: 'Nicht-bespielbar-Meldungen der letzten 90 Tage',
      subtitle: mttrP1 ? `Ø behoben nach ${formatDurationMs(mttrP1)}` : undefined,
    };
    const cardWeekTrend = {
      type: 'kpi',
      kicker: 'Meldungen',
      scope: 'week',
      bigText:
        weekDelta !== null
          ? `${weekDelta > 0 ? '+' : ''}${weekDelta} %`
          : thisWeek
            ? `${thisWeek.created}`
            : '–',
      unit: weekDelta !== null ? '' : thisWeek ? 'diese Woche' : '',
      title:
        weekDelta !== null
          ? `Meldungen vs. Vorwoche ${weekDelta > 0 ? 'gestiegen' : weekDelta < 0 ? 'gesunken' : 'stabil'}`
          : 'Meldungen dieser Woche',
      subtitle:
        thisWeek && prevWeek
          ? `Diese Woche ${thisWeek.created} · Vorwoche ${prevWeek.created}`
          : undefined,
      bigColor: weekDelta !== null && weekDelta > 25 ? accentByPriority('p2') : '#fff',
    };
    const cardEscalations = {
      type: 'kpi',
      kicker: 'Eskalationen',
      scope: '90d',
      big: escalationTotal,
      unit: 'Eskalationsketten',
      title: 'Eskalationen der letzten 90 Tage',
      subtitle:
        escalationExhausted > 0
          ? `${escalationExhausted} davon ohne Reaktion durchgelaufen`
          : 'Alle Ketten wurden beantwortet',
      bigColor: escalationExhausted > 0 ? accentByPriority('p1') : '#fff',
    };
    const cardEscalationSpeed = {
      type: 'kpi',
      kicker: 'Eskalationen',
      scope: '90d',
      bigText: escalationAck?.avgTimeToAckMs ? formatDurationMs(escalationAck.avgTimeToAckMs) : '–',
      unit: '',
      title: 'Ø Zeit bis zur Reaktion in der Kette',
      subtitle: escalationAck
        ? `Ø ${Math.round((escalationAck.avgSmsSent || 0) * 10) / 10} SMS bis zur Reaktion`
        : undefined,
    };
    const cardOldestIssue = {
      type: 'kpi',
      kicker: 'Live-Status',
      scope: 'live',
      bigText: oldestOpenIssue ? formatDurationMs(oldestOpenIssue.age) : '–',
      unit: '',
      title: oldestOpenIssue
        ? `Ältestes offenes Issue: ${oldestOpenIssue.issue.title}`
        : 'Keine offenen Issues',
      subtitle: oldestOpenIssue
        ? ISSUE_PRIORITY_BY_KEY[oldestOpenIssue.issue.priority]?.label
        : undefined,
    };
    const cardSuggestions = {
      type: 'kpi',
      kicker: 'Team-Input',
      scope: 'live',
      big: openIssues.filter(issue => issue.priority === 'p5').length,
      unit: 'Verbesserungsvorschläge',
      title: 'Ideen aus dem Team (P5)',
      subtitle: 'Über das Issue-Board einsehbar',
    };

    // --- decks per perspective ---------------------------------------------
    const deck = [];
    if (perspective === 'arena') {
      deck.push(kpiOpen, cardOutages, cardOpenByPriority);
      openIssues
        .filter(issue => ['p1', 'p2'].includes(issue.priority))
        .slice(0, 6)
        .forEach(issue => deck.push({ type: 'issue', issue }));
      deck.push(
        cardLongestOutage,
        cardReactionP1,
        cardMttrP1,
        cardCreated90,
        cardResolutionRate,
        cardOldestIssue,
        cardSuggestions
      );
    } else if (perspective === 'it') {
      deck.push(kpiOpen);
      p1Issues.slice(0, 5).forEach(issue => deck.push({ type: 'issue', issue }));
      deck.push(
        cardCategories,
        cardOpenByPriority,
        cardReactionP1,
        cardReactionAll,
        cardMttrP1,
        cardMttrP2,
        cardMttrAll,
        cardEscalations,
        cardEscalationSpeed,
        cardCreated90,
        cardWeekTrend,
        cardOldestIssue,
        cardLongestOutage
      );
    } else {
      deck.push(
        kpiOpen,
        cardDowntimeTotal,
        cardOutageCount,
        cardLongestOutage,
        cardMttrAll,
        cardMttrP1,
        cardReactionP1,
        cardResolutionRate,
        cardCreated90,
        cardP1Volume,
        cardWeekTrend,
        cardEscalations,
        cardEscalationSpeed,
        cardDowntimeRooms,
        cardCategories,
        cardOutages
      );
    }
    return deck;
  }, [issues, outages, stats, perspective]);

  // Auto-advance.
  useEffect(() => {
    if (paused || cards.length <= 1) return undefined;
    const interval = setInterval(
      () => setCardIndex(index => (index + 1) % cards.length),
      CARD_INTERVAL_MS
    );
    return () => clearInterval(interval);
  }, [paused, cards.length]);

  // A click anywhere on the stage advances to the next card. Controls (buttons,
  // perspective chips, progress dots) are excluded so they keep their own
  // behaviour.
  const handleStageClick = useCallback(
    event => {
      if (event.target.closest && event.target.closest('button, a, [data-no-advance]')) return;
      if (cards.length > 1) setCardIndex(index => (index + 1) % cards.length);
    },
    [cards.length]
  );

  useEffect(() => {
    setCardIndex(0);
  }, [perspective, cards.length]);

  // Keyboard navigation.
  useEffect(() => {
    const onKey = event => {
      if (event.key === 'ArrowRight')
        setCardIndex(index => (index + 1) % Math.max(1, cards.length));
      if (event.key === 'ArrowLeft')
        setCardIndex(index => (index - 1 + Math.max(1, cards.length)) % Math.max(1, cards.length));
      if (event.key === ' ') {
        event.preventDefault();
        setPaused(current => !current);
      }
      if (event.key === '1') setPerspective('arena');
      if (event.key === '2') setPerspective('it');
      if (event.key === '3') setPerspective('gf');
      // Escape leaves the presentation mode. While in browser fullscreen the
      // first Escape only exits fullscreen (native behaviour), the next one
      // closes the view.
      if (event.key === 'Escape' && !document.fullscreenElement) {
        navigate('/issues');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [cards.length, navigate]);

  useEffect(() => {
    const onFullscreenChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    } else if (rootRef.current) {
      rootRef.current.requestFullscreen().catch(() => {});
    }
  }, []);

  if (!canRead) return <Navigate to='/' replace />;

  const activePerspective = PERSPECTIVES.find(p => p.key === perspective);
  const card = cards[Math.min(cardIndex, Math.max(0, cards.length - 1))];

  return (
    <Box
      ref={rootRef}
      onClick={handleStageClick}
      sx={{
        cursor: 'pointer',
        position: 'fixed',
        inset: 0,
        zIndex: theme => theme.zIndex.modal + 10,
        display: 'flex',
        flexDirection: 'column',
        color: '#fff',
        background:
          'radial-gradient(1200px 800px at 15% 10%, rgba(0,64,221,0.35), transparent 60%), radial-gradient(1000px 700px at 85% 90%, rgba(255,214,0,0.14), transparent 60%), linear-gradient(160deg, #05070f 0%, #0b1026 55%, #05070f 100%)',
        backgroundSize: '200% 200%',
        animation: 'fzBg 30s ease-in-out infinite alternate',
        '@keyframes fzBg': {
          from: { backgroundPosition: '0% 0%' },
          to: { backgroundPosition: '100% 100%' },
        },
      }}
    >
      {/* Top bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 4, py: 2.5 }}>
        <Typography
          sx={{
            fontWeight: 800,
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            fontSize: 14,
            color: 'rgba(255,255,255,0.7)',
          }}
        >
          FunZone · Mängel & Störungen
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Box data-no-advance sx={{ display: 'flex', gap: 1 }}>
          {PERSPECTIVES.map(item => (
            <Chip
              key={item.key}
              label={item.label}
              onClick={() => setPerspective(item.key)}
              sx={{
                cursor: 'pointer',
                fontWeight: 700,
                transition: 'all 300ms',
                ...(perspective === item.key
                  ? { bgcolor: item.accent, color: '#0a0a0a' }
                  : { color: 'rgba(255,255,255,0.75)', bgcolor: 'rgba(255,255,255,0.08)' }),
              }}
            />
          ))}
        </Box>
        <Typography
          sx={{ fontVariantNumeric: 'tabular-nums', fontSize: 20, fontWeight: 700, ml: 2 }}
        >
          {clock.format('HH:mm:ss')}
        </Typography>
        <Tooltip title={paused ? 'Fortsetzen (Leertaste)' : 'Pausieren (Leertaste)'}>
          <IconButton onClick={() => setPaused(current => !current)} sx={{ color: '#fff' }}>
            {paused ? <PlayArrowIcon /> : <PauseIcon />}
          </IconButton>
        </Tooltip>
        <Tooltip title='Vollbild'>
          <IconButton onClick={toggleFullscreen} sx={{ color: '#fff' }}>
            {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
        </Tooltip>
        <Tooltip title='Schließen'>
          <IconButton onClick={() => navigate('/issues')} sx={{ color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Stage */}
      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
          position: 'relative',
        }}
      >
        <IconButton
          onClick={() => setCardIndex(index => (index - 1 + cards.length) % cards.length)}
          sx={{
            position: 'absolute',
            left: 16,
            color: 'rgba(255,255,255,0.6)',
            '&:hover': { color: '#fff' },
          }}
        >
          <ChevronLeftIcon fontSize='large' />
        </IconButton>
        {card ? (
          <Box
            key={`${perspective}-${cardIndex}`}
            sx={{ display: 'flex', justifyContent: 'center' }}
          >
            <PresentationCard card={card} accent={activePerspective.accent} />
          </Box>
        ) : (
          <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 24 }}>Lade Daten…</Typography>
        )}
        <IconButton
          onClick={() => setCardIndex(index => (index + 1) % cards.length)}
          sx={{
            position: 'absolute',
            right: 16,
            color: 'rgba(255,255,255,0.6)',
            '&:hover': { color: '#fff' },
          }}
        >
          <ChevronRightIcon fontSize='large' />
        </IconButton>
      </Box>

      {/* Footer: progress */}
      <Box
        data-no-advance
        sx={{
          px: 4,
          pb: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        {!paused && cards.length > 1 && (
          <Box
            key={`${perspective}-${cardIndex}-progress`}
            sx={{
              width: 'min(480px, 70vw)',
              height: 3,
              borderRadius: 2,
              bgcolor: 'rgba(255,255,255,0.12)',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                height: '100%',
                bgcolor: activePerspective.accent,
                animation: `fzProgress ${CARD_INTERVAL_MS}ms linear both`,
                '@keyframes fzProgress': { from: { width: 0 }, to: { width: '100%' } },
              }}
            />
          </Box>
        )}
        <Box sx={{ display: 'flex', gap: 1 }}>
          {cards.map((entry, index) => (
            <Box
              key={index}
              onClick={() => setCardIndex(index)}
              sx={{
                width: index === cardIndex ? 26 : 8,
                height: 8,
                borderRadius: 4,
                cursor: 'pointer',
                transition: 'all 300ms cubic-bezier(0.22,1,0.36,1)',
                bgcolor: index === cardIndex ? activePerspective.accent : 'rgba(255,255,255,0.25)',
              }}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default IssuePresentationView;
